if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.swiperOptions = swiperOptions;
      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent =
        this.closest('.product-feed[data-section-id]') ||
        this.closest('[data-section-id]');
      this.fetchController = null;
      this.handleResize = this.syncHeights.bind(this);
      this.resizeListening = false;

      this.sliderOptions = {
        a11y: false,
        threshold: 1,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: swiperOptions.slidesPerViewMobile || 'auto',

        loop: false,
        rewind: false,
        centeredSlides: false,
        centeredSlidesBounds: false,
        slideToClickedSlide: false,

        followFinger: true,
        allowTouchMove: true,
        touchStartPreventDefault: false,
        touchMoveStopPropagation: false,
        touchReleaseOnEdges: true,
        touchRatio: 1,
        resistance: true,
        resistanceRatio: 0.95,

        preventClicks: false,
        preventClicksPropagation: false,
        watchOverflow: true,
        watchSlidesProgress: true,

        shortSwipes: true,
        longSwipes: true,
        longSwipesRatio: 0.15,
        longSwipesMs: 150,

        freeMode: {
          enabled: true,
          momentum: true,
          momentumRatio: 0.8,
          momentumVelocityRatio: 0.8,
          momentumBounce: false,
          sticky: false
        },

        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2,
            freeMode: {
              enabled: false
            }
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2,
            freeMode: {
              enabled: false
            }
          }
        }
      };

      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }

      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }

      this.initSlider();
      this.syncHeights();
      window.addEventListener('resize', this.handleResize);
      this.resizeListening = true;
    }

    disconnectedCallback() {
      if (this.fetchController) {
        this.fetchController.abort();
      }

      if (this.resizeListening) {
        window.removeEventListener('resize', this.handleResize);
        this.resizeListening = false;
      }
    }

    syncHeights() {
      const textSlide = this.querySelector('.products-feed__card-text-custom-slide');
      const actionElement = this.querySelector('.product-card__actions');

      if (textSlide && actionElement) {
        const actionHeight = actionElement.offsetHeight;
        textSlide.style.height = `${actionHeight}px`;
      }
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');

      const requestUrl = this.getAttribute('data-url');
      const requestedProductId = this.dataset.recommendationProductId || '';

      if (this.fetchController) {
        this.fetchController.abort();
      }
      this.fetchController = new AbortController();

      fetch(requestUrl, {
        cache: requestedProductId ? 'no-store' : 'default',
        signal: this.fetchController.signal
      })
        .then(response => response.text())
        .then(text => {
          if (
            requestedProductId &&
            requestedProductId !== this.dataset.recommendationProductId
          ) {
            return;
          }

          const html = new DOMParser().parseFromString(text, 'text/html');
          const wrapper = html.querySelector('.swiper-wrapper');

          if (wrapper && wrapper.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML = wrapper.innerHTML;
            this.parent.classList.remove('hidden');
            this.initSlider();
            this.syncHeights();
          }
        })
        .catch(e => {
          if (e.name === 'AbortError') return;
          console.error(e);
        });
    }

    initSlider(slider = this) {
      this.slider = new Swiper(slider, this.sliderOptions);

      this.slider.wrapperEl.setAttribute('aria-live', 'polite');
      this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;

      this.slider.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute(
          'aria-label',
          `${i + 1} / ${this.slider.slides.length}`
        );
        slide.dataset.index = i;
      });

      if (this.swiperOptions?.navigation && this.slider.navigation) {
        const sliderButtons = [
          this.slider.navigation.prevEl,
          this.slider.navigation.nextEl
        ];

        sliderButtons.forEach((button, i) => {
          if (!button) return;

          const type = i === 1 ? 'Next' : 'Previous';

          button.setAttribute('aria-label', `${type} slide`);
          button.setAttribute('aria-controls', this.slider.wrapperEl.id);
        });
      }

      if (this.slider.autoplay) {
        this.slider.wrapperEl.addEventListener('mouseenter', () => {
          this.slider.autoplay.stop();
        });

        this.slider.wrapperEl.addEventListener('mouseleave', () => {
          if (
            !this.classList.contains('product--open-on-quick-cart') &&
            this.sliderOptions.autoplay
          ) {
            this.slider.autoplay.start();
          }

          if (this.swiperOptions.autoplay === false) {
            this.slider.autoplay.stop();
          }
        });
      }

      if (
        this.hasAttribute('data-init-quick-cart') &&
        document.querySelector('quick-cart-drawer') &&
        this.querySelector('.swiper-slide')
      ) {
        document.querySelector('quick-cart-drawer').init();
      }

      this.querySelectorAll('product-card').forEach(card => {
        card.init();
      });

      if (Shopify.designMode) {
        if (
          this.hasAttribute('data-init-quick-cart') &&
          document.querySelector('quick-cart-drawer') &&
          this.querySelector('.swiper-slide')
        ) {
          document.querySelector('quick-cart-drawer').init();
        }
      }
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}
